import React, { useState } from 'react';
import Helmet from '../components/Helmet/Helmet';
import { Link, useLocation } from 'react-router-dom';
import '../styles/login.css';
import { sendPasswordResetEmail } from 'firebase/auth';
import { auth } from '../firebase.config';
import { toast } from 'react-toastify';
import { motion } from 'framer-motion';
import { getAuthErrorMessage } from '../services/authErrors';
import bannerImg from '../assets/images/banner-1.jpg';

const ForgotPassword = () => {
  const location = useLocation();
  const [email, setEmail] = useState(location.state?.email || '');
  const [loading, setLoading] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setErrorMessage('');

    if (!email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      const msg = 'Please enter a valid email address.';
      setErrorMessage(msg);
      toast.error(msg);
      return;
    }

    setLoading(true);

    try {
      await sendPasswordResetEmail(auth, email.trim());
      setLoading(false);
      setIsSubmitted(true);
      toast.success('Password reset link sent! Check your inbox.');
    } catch (error) {
      setLoading(false);
      const friendlyError = getAuthErrorMessage(error);
      setErrorMessage(friendlyError);
      toast.error(friendlyError);
    }
  };

  return (
    <Helmet title="Forgot Password | L'HABILLEUR">
      <section className="auth__section">
        <div className="auth__container-wide">
          <motion.div 
            className="auth__card-split"
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.35 }}
          >
            {/* Side Showcase Banner */}
            <div 
              className="auth__banner-side"
              style={{ backgroundImage: `url(${bannerImg})` }}
            >
              <div className="auth__banner-overlay"></div>
              <div className="auth__banner-content">
                <div className="auth__banner-brand">
                  <h2>L'HABILLEUR</h2>
                  <p>Account Security & Password Recovery</p>
                </div>

                <div className="auth__banner-features">
                  <div className="auth__feature-item">
                    <i className="ri-shield-keyhole-line"></i>
                    <span>Secure End-to-End Encryption</span>
                  </div>
                  <div className="auth__feature-item">
                    <i className="ri-mail-send-line"></i>
                    <span>Instant Recovery Link Delivery</span>
                  </div>
                  <div className="auth__feature-item">
                    <i className="ri-lock-password-line"></i>
                    <span>Safe Account Protection</span>
                  </div>
                </div>

                <div className="auth__banner-footer">
                  <i className="ri-shield-check-fill"></i>
                  <span>256-bit SSL encrypted verification</span>
                </div>
              </div>
            </div>

            {/* Form Side */}
            <div className="auth__form-side">
              {!isSubmitted ? (
                <>
                  <div className="auth__header-compact">
                    <h2>Reset Password</h2>
                    <p>Enter your email and we'll send you a link to reset your password</p>
                  </div>

                  {errorMessage && (
                    <div className="auth__alert">
                      <i className="ri-error-warning-fill"></i>
                      <div>{errorMessage}</div>
                    </div>
                  )}

                  <form onSubmit={handleResetPassword} noValidate>
                    <div className="auth__form-group">
                      <label htmlFor="reset-email">Email Address</label>
                      <div className="auth__input-wrapper">
                        <i className="ri-mail-line auth__input-icon"></i>
                        <input
                          id="reset-email"
                          type="email"
                          className="auth__input"
                          placeholder="name@example.com"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          required
                          autoFocus
                        />
                      </div>
                    </div>

                    <button type="submit" className="auth__submit-btn" disabled={loading}>
                      {loading ? (
                        <>
                          <i className="ri-loader-4-line ri-spin"></i> Sending Link...
                        </>
                      ) : (
                        <>
                          Send Reset Link <i className="ri-send-plane-fill"></i>
                        </>
                      )}
                    </button>

                    <div className="auth__footer-text">
                      Remember your password? <Link to="/login">Back to Sign In</Link>
                    </div>
                  </form>
                </>
              ) : (
                /* Success View */
                <div style={{ textAlign: 'center', padding: '20px 0' }}>
                  <div 
                    style={{
                      width: '68px',
                      height: '68px',
                      borderRadius: '50%',
                      background: '#ecfdf5',
                      color: '#10b981',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      margin: '0 auto 18px',
                      fontSize: '2rem'
                    }}
                  >
                    <i className="ri-mail-check-line"></i>
                  </div>

                  <h3 style={{ fontSize: '1.45rem', fontWeight: 700, color: 'var(--primary-color)', marginBottom: '8px' }}>
                    Check Your Inbox
                  </h3>
                  <p style={{ color: '#64748b', fontSize: '0.9rem', marginBottom: '22px', lineHeight: 1.5 }}>
                    We have sent a secure password reset link to: <br />
                    <strong style={{ color: '#1e293b' }}>{email}</strong>
                  </p>

                  <p style={{ color: '#94a3b8', fontSize: '0.82rem', marginBottom: '22px' }}>
                    Didn't receive the email? Check your spam folder or click below to try again.
                  </p>

                  <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    <button 
                      type="button" 
                      onClick={() => setIsSubmitted(false)}
                      className="auth__submit-btn"
                      style={{ background: '#f1f5f9', color: '#1e293b', boxShadow: 'none' }}
                    >
                      <i className="ri-refresh-line"></i> Resend Email
                    </button>

                    <Link to="/login" style={{ textDecoration: 'none' }}>
                      <button type="button" className="auth__submit-btn">
                        Back to Sign In <i className="ri-arrow-right-line"></i>
                      </button>
                    </Link>
                  </div>
                </div>
              )}
            </div>
          </motion.div>
        </div>
      </section>
    </Helmet>
  );
};

export default ForgotPassword;
